import type { DayRow, ProgressRecord, Settings, UserInfo } from "../../shared/model";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown, keepalive = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      keepalive,
      headers: { "x-ponder": "1", ...(body !== undefined ? { "content-type": "application/json" } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined
    });
  } catch {
    throw new ApiError(0, "You seem to be offline.");
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string } | null)?.error || `Request failed (${res.status})`);
  return data as T;
}

/** The password never leaves the browser: this is what we send instead (PBKDF2-SHA256, salted with the e-mail). */
export async function deriveKey(password: string, email: string): Promise<string> {
  const enc = new TextEncoder();
  const base = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: enc.encode("ponder/v1|" + email.trim().toLowerCase()), iterations: 310000 },
    base,
    256
  );
  const u8 = new Uint8Array(bits);
  let s = "";
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export interface SyncRequest {
  device: string;
  since: number;
  progress: ProgressRecord[];
  days: DayRow[];
  settings: { value: Settings; at: number } | null;
}

export interface SyncResponse {
  now: number;
  progress: ProgressRecord[];
  days: DayRow[];
  settings: { value: Partial<Settings>; at: number };
  name: string;
}

export const api = {
  me: () => call<{ user: UserInfo | null }>("GET", "/api/auth/me"),
  signup: (email: string, name: string, key: string) => call<{ user: UserInfo; recoveryKey: string }>("POST", "/api/auth/signup", { email, name, key }),
  login: (email: string, key: string) => call<{ user: UserInfo }>("POST", "/api/auth/login", { email, key }),
  logout: () => call<{ ok: true }>("POST", "/api/auth/logout", {}),
  recover: (email: string, recoveryKey: string, key: string) => call<{ user: UserInfo; recoveryKey: string }>("POST", "/api/auth/recover", { email, recoveryKey, key }),
  changePassword: (oldKey: string, newKey: string) => call<{ ok: true }>("POST", "/api/auth/password", { oldKey, newKey }),
  newRecoveryKey: (key: string) => call<{ recoveryKey: string }>("POST", "/api/auth/recovery-key", { key }),
  updateProfile: (patch: { name?: string }) => call<{ user: UserInfo }>("PATCH", "/api/auth/profile", patch),
  deleteAccount: (key: string) => call<{ ok: true }>("POST", "/api/auth/delete", { key }),
  sync: (req: SyncRequest, keepalive = false) => call<SyncResponse>("POST", "/api/sync", req, keepalive)
};
